import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Settings, Upload, Save, Palette, FormInput, CheckSquare, Mail } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { type ConfiguracaoSolucao } from "@/lib/fakeApi";
import { queueApiService, type OptinConfig } from "@/services/queueApi.service";
import { CustomFieldsBuilder, type CustomFieldConfig } from "@/components/queue/CustomFieldsBuilder";
import { OptinsBuilder } from "@/components/queue/OptinsBuilder";
import { EmailTemplateEditor } from "@/components/queue/EmailTemplateEditor";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";

export default function EventsConfiguracoes() {
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const { eventId: contextEventId } = useAuth();
  const url = searchParams.get('url');
  // Use eventId from context if not in URL params
  const eventId = searchParams.get('eventId') || contextEventId || '';

  const [formData, setFormData] = useState<Partial<ConfiguracaoSolucao>>({
    corPrincipal: '#4369F5',
    corTexto: '#FFFFFF',
    logo: '',
    imagemFundo: '',
    termosUso: '',
    modoAgendamento: false,
  });

  const [customFields, setCustomFields] = useState<CustomFieldConfig[]>([]);
  const [optins, setOptins] = useState<OptinConfig[]>([]);
  const [logoPreview, setLogoPreview] = useState<string>('');
  const [imagemFundoPreview, setImagemFundoPreview] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isSavingFields, setIsSavingFields] = useState(false);
  const [isSavingOptins, setIsSavingOptins] = useState(false);

  // Cores predefinidas para seleção
  const coresPredefinidas = [
    { nome: 'Azul Padrão', valor: '#4369F5' },
    { nome: 'Roxo', valor: '#8B5CF6' },
    { nome: 'Verde', valor: '#10B981' },
    { nome: 'Laranja', valor: '#F59E0B' },
    { nome: 'Rosa', valor: '#EC4899' },
    { nome: 'Vermelho', valor: '#EF4444' },
    { nome: 'Índigo', valor: '#6366F1' },
    { nome: 'Teal', valor: '#14B8A6' },
  ];

  useEffect(() => {
    loadConfiguracoes();
    loadCustomFields();
    loadOptins();
  }, [eventId]);

  const loadConfiguracoes = async () => {
    try {
      setIsLoading(true);
      const config = await queueApiService.getConfiguracaoSolucao();
      setFormData((prev) => ({
        ...{ corPrincipal: '#4369F5', corTexto: '#FFFFFF', logo: '', imagemFundo: '', termosUso: '' },
        ...prev,
        ...config,
      }));
      setLogoPreview(config.logo);
      setImagemFundoPreview(config.imagemFundo);
    } catch (error) {
      toast({
        title: "Erro",
        description: "Não foi possível carregar as configurações",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const loadCustomFields = async () => {
    if (!eventId) return;

    try {
      const fields = await queueApiService.getEventCustomFields(eventId);
      setCustomFields(fields || []);
    } catch (error) {
      // If endpoint doesn't exist yet, just use empty array
      setCustomFields([]);
    }
  };

  const loadOptins = async () => {
    if (!eventId) return;

    try {
      const data = await queueApiService.getEventOptins(eventId);
      setOptins(data || []);
    } catch {
      setOptins([]);
    }
  };

  const handleSaveOptins = async () => {
    if (!eventId) {
      toast({
        title: "Erro",
        description: "ID do evento não encontrado",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsSavingOptins(true);
      await queueApiService.updateEventOptins(eventId, optins);

      toast({
        title: "Sucesso",
        description: "Opt-ins salvos com sucesso!",
      });
    } catch (error) {
      toast({
        title: "Erro",
        description: "Não foi possível salvar os opt-ins",
        variant: "destructive",
      });
    } finally {
      setIsSavingOptins(false);
    }
  };

  const handleInputChange = (field: keyof ConfiguracaoSolucao, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFileUpload = async (field: 'logo' | 'imagemFundo', event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file size (10MB max)
      if (file.size > 10 * 1024 * 1024) {
        toast({
          title: "Erro",
          description: "O arquivo deve ter no máximo 10MB",
          variant: "destructive",
        });
        return;
      }

      // Validate file type
      const validTypes = ['image/png', 'image/jpeg', 'image/jpg'];
      if (!validTypes.includes(file.type)) {
        toast({
          title: "Erro",
          description: "Apenas arquivos PNG, JPG e JPEG são permitidos",
          variant: "destructive",
        });
        return;
      }

      try {
        // Show preview immediately for better UX
        const reader = new FileReader();
        reader.onload = (e) => {
          const result = e.target?.result as string;
          if (field === 'logo') {
            setLogoPreview(result);
          } else {
            setImagemFundoPreview(result);
          }
        };
        reader.readAsDataURL(file);

        // Upload file to server
        const uploadType = field === 'logo' ? 'logo' : 'background';
        const response = await queueApiService.uploadConfigurationImage(file, uploadType);
        
        // Update form data with the uploaded URL
        handleInputChange(field, response.url);
        
        toast({
          title: "Sucesso",
          description: `${field === 'logo' ? 'Logo' : 'Imagem de fundo'} enviado(a) com sucesso!`,
        });
      } catch (error) {
        toast({
          title: "Erro",
          description: `Falha ao enviar ${field === 'logo' ? 'logo' : 'imagem de fundo'}`,
          variant: "destructive",
        });
        // Reset preview on error
        if (field === 'logo') {
          setLogoPreview(formData.logo || '');
        } else {
          setImagemFundoPreview(formData.imagemFundo || '');
        }
      }
    }
  };

  const handleSaveCustomFields = async () => {
    if (!eventId) {
      toast({
        title: "Erro",
        description: "ID do evento não encontrado",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsSavingFields(true);
      await queueApiService.updateEventCustomFields(eventId, customFields);

      toast({
        title: "Sucesso",
        description: "Campos personalizados salvos com sucesso!",
      });
    } catch (error) {
      toast({
        title: "Erro",
        description: "Não foi possível salvar os campos personalizados",
        variant: "destructive",
      });
    } finally {
      setIsSavingFields(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Permitir salvar sem termos obrigatórios (cores e identidade visual independentes)

    try {
      setIsSaving(true);
      await queueApiService.saveConfiguracaoSolucao({
        id: formData.id || 'config1',
        corPrincipal: formData.corPrincipal || '#4369F5',
        corTexto: formData.corTexto || '#FFFFFF',
        logo: formData.logo || '',
        imagemFundo: formData.imagemFundo || '',
        termosUso: formData.termosUso || '',
        modoAgendamento: formData.modoAgendamento ?? false,
        emailCompletionTemplate: formData.emailCompletionTemplate,
      });
      
      toast({
        title: "Sucesso",
        description: "Configurações salvas com sucesso!",
      });
    } catch (error) {
      toast({
        title: "Erro",
        description: "Não foi possível salvar as configurações",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-muted rounded w-64 mb-4"></div>
          <div className="space-y-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-20 bg-muted rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <div className="flex h-10 w-10 flex-none items-center justify-center rounded-[var(--radius)] bg-brand-logo-3/10">
          <Settings className="text-brand-logo-3" size={20} />
        </div>
        <div className="flex-1">
          <h1 className="text-[16px] font-bold text-foreground">Configurações da Solução</h1>
          <p className="text-[13px] text-muted-foreground">
            Personalize a identidade visual e configurações da sua solução
          </p>
          {url && (
            <p className="text-sm text-brand-logo-1 mt-1">
              Conectado a: {decodeURIComponent(url)}
            </p>
          )}
        </div>
      </div>

      {/* Tabs for different configuration sections */}
      <Tabs defaultValue="appearance" className="space-y-6">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="appearance">Aparência</TabsTrigger>
          <TabsTrigger value="custom-fields" className="flex items-center gap-2">
            <FormInput className="w-4 h-4" />
            Campos Personalizados
          </TabsTrigger>
          <TabsTrigger value="optins" className="flex items-center gap-2">
            <CheckSquare className="w-4 h-4" />
            Opt-ins
          </TabsTrigger>
          <TabsTrigger value="email" className="flex items-center gap-2">
            <Mail className="w-4 h-4" />
            Email
          </TabsTrigger>
        </TabsList>

        {/* Appearance Tab */}
        <TabsContent value="appearance">
          <form onSubmit={handleSubmit} className="space-y-6">
        {/* Cor Principal */}
        <div className="bg-card p-8 rounded-xl border border-border">
          <div className="flex items-center gap-3 mb-6">
            <Palette className="text-brand-logo-3" size={24} />
            <div>
              <h3 className="text-[16px] font-semibold text-foreground">Cor Principal</h3>
              <p className="text-muted-foreground">
                Esta cor será usada para dar identidade visual à sua solução
              </p>
            </div>
          </div>

          {/* Preview da cor atual */}
          <div className="mb-6">
            <div className="flex items-center gap-4 p-4 bg-muted rounded-xl">
              <div 
                className="w-16 h-16 rounded-xl border-2 border-border shadow-lg"
                style={{ backgroundColor: formData.corPrincipal }}
              ></div>
              <div>
                <p className="text-foreground font-medium">Cor Selecionada</p>
                <p className="text-muted-foreground text-sm">{formData.corPrincipal}</p>
              </div>
            </div>
          </div>

          {/* Seleção de cores em linha */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Cor personalizada - primeira coluna */}
            <div className="space-y-4">
              <h4 className="text-foreground font-medium">Escolha sua cor personalizada</h4>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={formData.corPrincipal || '#4369F5'}
                  onChange={(e) => handleInputChange('corPrincipal', e.target.value)}
                  className="w-16 h-12 rounded-lg border border-border cursor-pointer"
                />
                <input
                  type="text"
                  value={formData.corPrincipal || '#4369F5'}
                  onChange={(e) => handleInputChange('corPrincipal', e.target.value)}
                  placeholder="#4369F5"
                  className="flex-1 px-4 py-2 bg-muted border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-brand-logo-1"
                />
              </div>
            </div>

            {/* Cores predefinidas - segunda coluna */}
            <div className="space-y-4">
              <h4 className="text-foreground font-medium">Ou cores predefinidas</h4>
              <div className="grid grid-cols-4 gap-3">
                {coresPredefinidas.map((cor) => (
                  <button
                    key={cor.valor}
                    type="button"
                    onClick={() => handleInputChange('corPrincipal', cor.valor)}
                    className={`w-12 h-12 rounded-xl border-2 transition-all hover:scale-110 ${
                      formData.corPrincipal === cor.valor 
                        ? 'border-border shadow-lg ring-2 ring-offset-2 ring-brand-logo-1' 
                        : 'border-border hover:border-border'
                    }`}
                    style={{ backgroundColor: cor.valor }}
                    title={cor.nome}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Cor do Texto */}
        <div className="bg-card p-8 rounded-xl border border-border">
          <div className="flex items-center gap-3 mb-6">
            <Palette className="text-brand-logo-3" size={24} />
            <div>
              <h3 className="text-[16px] font-semibold text-foreground">Cor do Texto</h3>
              <p className="text-muted-foreground">
                Esta cor será aplicada aos textos na página do usuário
              </p>
            </div>
          </div>

          {/* Preview da cor do texto */}
          <div className="mb-6">
            <div className="flex items-center gap-4 p-4 bg-muted rounded-xl">
              <div 
                className="w-16 h-16 rounded-xl border-2 border-border shadow-lg flex items-center justify-center"
                style={{ backgroundColor: 'var(--bg-dark)' }}
              >
                <span style={{ color: formData.corTexto || 'var(--text-on-dark)' }}>Aa</span>
              </div>
              <div>
                <p className="text-foreground font-medium">Cor Selecionada</p>
                <p className="text-muted-foreground text-sm">{formData.corTexto}</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Cor personalizada */}
            <div className="space-y-4">
              <h4 className="text-foreground font-medium">Escolha sua cor personalizada</h4>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={formData.corTexto || '#FFFFFF'}
                  onChange={(e) => handleInputChange('corTexto', e.target.value as any)}
                  className="w-16 h-12 rounded-lg border border-border cursor-pointer"
                />
                <input
                  type="text"
                  value={formData.corTexto || '#FFFFFF'}
                  onChange={(e) => handleInputChange('corTexto', e.target.value as any)}
                  placeholder="#FFFFFF"
                  className="flex-1 px-4 py-2 bg-muted border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-brand-logo-1"
                />
              </div>
            </div>

            {/* Cores predefinidas */}
            <div className="space-y-4">
              <h4 className="text-foreground font-medium">Ou cores predefinidas</h4>
              <div className="grid grid-cols-4 gap-3">
                {['#FFFFFF', '#E5E7EB', '#FDE68A', '#C7D2FE', '#FBCFE8', '#A7F3D0', '#FCA5A5', '#F59E0B'].map((cor) => (
                  <button
                    key={cor}
                    type="button"
                    onClick={() => handleInputChange('corTexto', cor as any)}
                    className={`w-12 h-12 rounded-xl border-2 transition-all hover:scale-110 ${
                      formData.corTexto === cor 
                        ? 'border-border shadow-lg ring-2 ring-offset-2 ring-brand-logo-1' 
                        : 'border-border hover:border-border'
                    }`}
                    style={{ backgroundColor: 'var(--bg-dark)', color: cor }}
                    title={cor}
                  >
                    Aa
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Upload de Arquivos */}
        <div className="bg-card p-8 rounded-xl border border-border">
          <h3 className="text-[16px] font-semibold text-foreground mb-6">Identidade Visual</h3>
          
          <div className="space-y-8">
            {/* Logo */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
              <div>
                <label className="block text-foreground font-medium mb-2">
                  Logo da Solução
                </label>
                <div className="text-sm text-muted-foreground space-y-1">
                  <p>• Use PNG ou JPG com boa resolução.</p>
                  <p>• Tamanho: 120×37px</p>
                  <p>• Prefira fundo transparente.</p>
                  <p>• Garanta contraste com a cor escolhida para visibilidade.</p>
                  <p>• Evite detalhes excessivos.</p>
                </div>
              </div>
              
              <div className="relative">
                <div className="w-full h-32 bg-muted border-2 border-dashed border-border rounded-xl flex items-center justify-center overflow-hidden">
                  {logoPreview ? (
                    <img 
                      src={logoPreview} 
                      alt="Logo preview" 
                      className="max-w-full max-h-full object-contain"
                    />
                  ) : (
                    <div className="text-center">
                      <Upload className="text-muted-foreground mx-auto mb-2" size={32} />
                      <p className="text-muted-foreground text-sm">Clique para fazer upload do logo</p>
                    </div>
                  )}
                </div>
                <input
                  type="file"
                  accept=".png,.jpg,.jpeg"
                  onChange={(e) => handleFileUpload('logo', e)}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                />
              </div>
            </div>

            {/* Imagem de Fundo */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
              <div>
                <label className="block text-foreground font-medium mb-2">
                  Imagem de Fundo
                </label>
                <div className="text-sm text-muted-foreground space-y-1">
                  <p>• Use PNG ou JPG com boa resolução.</p>
                  <p>• Tamanho: 375×211px</p>
                </div>
              </div>
              
              <div className="relative">
                <div className="w-full h-32 bg-muted border-2 border-dashed border-border rounded-xl flex items-center justify-center overflow-hidden">
                  {imagemFundoPreview ? (
                    <img 
                      src={imagemFundoPreview} 
                      alt="Imagem de fundo preview" 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="text-center">
                      <Upload className="text-muted-foreground mx-auto mb-2" size={32} />
                      <p className="text-muted-foreground text-sm">Clique para fazer upload da imagem</p>
                    </div>
                  )}
                </div>
                <input
                  type="file"
                  accept=".png,.jpg,.jpeg"
                  onChange={(e) => handleFileUpload('imagemFundo', e)}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Termos de Uso */}
        <div className="bg-card p-8 rounded-xl border border-border">
          <h3 className="text-[16px] font-semibold text-foreground mb-6">Termos de Uso</h3>

          <div>
            <label className="block text-foreground font-medium mb-3">
              Termos de uso da solução *
            </label>
            <textarea
              value={formData.termosUso || ''}
              onChange={(e) => handleInputChange('termosUso', e.target.value)}
              placeholder="Digite os termos de uso que serão apresentados aos usuários da solução..."
              rows={12}
              className="w-full px-4 py-3 bg-muted border border-border rounded-xl text-foreground placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-brand-logo-1 focus:border-transparent resize-vertical"
            />
            <p className="text-sm text-muted-foreground mt-2">
              Defina as condições de uso, privacidade e responsabilidades para os usuários
            </p>
          </div>
        </div>

        {/* Modo de Exibição */}
        <div className="bg-card p-8 rounded-xl border border-border">
          <h3 className="text-[16px] font-semibold text-foreground mb-6">Modo de Exibição</h3>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-foreground font-medium">Modo Agendamento</p>
              <p className="text-sm text-muted-foreground mt-1">
                Quando ativado, a área pública exibirá terminologia de agendamento ao invés de fila
              </p>
            </div>
            <Switch
              checked={formData.modoAgendamento ?? false}
              onCheckedChange={(checked) => handleInputChange('modoAgendamento', checked)}
            />
          </div>
        </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-4">
              <button
                type="submit"
                disabled={isSaving}
                className="btn-primary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save size={20} />
                {isSaving ? 'Salvando...' : 'Salvar Configurações'}
              </button>
            </div>
          </form>
        </TabsContent>

        {/* Custom Fields Tab */}
        <TabsContent value="custom-fields">
          <div className="space-y-6">
            {eventId ? (
              <>
                <CustomFieldsBuilder
                  fields={customFields}
                  onChange={setCustomFields}
                  maxFields={20}
                />
                <div className="flex justify-end gap-4">
                  <button
                    onClick={handleSaveCustomFields}
                    disabled={isSavingFields}
                    className="btn-primary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Save size={20} />
                    {isSavingFields ? 'Salvando...' : 'Salvar Campos'}
                  </button>
                </div>
              </>
            ) : (
              <div className="bg-card p-8 rounded-xl border border-border text-center">
                <FormInput className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
                <h3 className="text-[16px] font-semibold text-foreground mb-2">Evento não selecionado</h3>
                <p className="text-muted-foreground">
                  Selecione um evento para configurar campos personalizados
                </p>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Opt-ins Tab */}
        <TabsContent value="optins">
          <div className="space-y-6">
            {eventId ? (
              <>
                <OptinsBuilder
                  optins={optins}
                  onChange={setOptins}
                  eventId={eventId}
                />
                <div className="flex justify-end gap-4">
                  <button
                    onClick={handleSaveOptins}
                    disabled={isSavingOptins}
                    className="btn-primary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Save size={20} />
                    {isSavingOptins ? 'Salvando...' : 'Salvar Opt-ins'}
                  </button>
                </div>
              </>
            ) : (
              <div className="bg-card p-8 rounded-xl border border-border text-center">
                <CheckSquare className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
                <h3 className="text-[16px] font-semibold text-foreground mb-2">Evento não selecionado</h3>
                <p className="text-muted-foreground">
                  Selecione um evento para configurar opt-ins
                </p>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Email Tab */}
        <TabsContent value="email">
          <div className="bg-card p-8 rounded-xl border border-border space-y-6">
            <EmailTemplateEditor
              value={formData.emailCompletionTemplate ?? { enabled: false, subject: '', htmlTemplate: '' }}
              onChange={(emailCompletionTemplate) =>
                setFormData((prev) => ({ ...prev, emailCompletionTemplate }))
              }
            />
            <div className="flex justify-end">
              <button
                onClick={handleSubmit as any}
                disabled={isSaving}
                className="btn-primary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save size={20} />
                {isSaving ? 'Salvando...' : 'Salvar Configurações'}
              </button>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}